<template>
  <div class="auth-page">
    <main class="auth-main">
      <section class="auth-copy" aria-labelledby="signup-title">
        <h1 id="signup-title" class="sr-only">
          {{ t('signup.title') }}
        </h1>
        <div class="auth-art" aria-hidden="true">
          <img src="/images/girl-focused.png" alt="">
        </div>
        <p class="auth-lede">
          {{ pageCopy.intro }}
        </p>
      </section>

      <section class="auth-panel" aria-label="Sign up form">
        <div class="form-section">
          <h2>{{ t('signup.title') }}</h2>

          <div class="no-account-notice">
            <div class="notice-content">
              <div class="notice-text">
                <strong>{{ t('signup.noAccountNeeded') }}</strong>
                <p>
                  {{ t('signup.noAccountDescriptionStart') }}
                  <router-link to="/card" class="notice-link">{{ t('signup.createAndShareCards') }}</router-link>
                  {{ t('signup.noAccountDescriptionEnd') }}
                </p>
              </div>
            </div>
          </div>

          <div v-if="errorMessage" class="error-message">
            <div>{{ errorMessage }}</div>
            <ul v-if="passwordErrors.length > 0" class="password-errors">
              <li v-for="(error, index) in passwordErrors" :key="index">{{ error }}</li>
            </ul>
          </div>

          <form @submit.prevent="handleSubmit" v-if="!successMessage">
            <div class="form-group">
              <label for="email">{{ t('signup.email') }}</label>
              <input 
                type="email" 
                id="email" 
                v-model="email" 
                required
                :placeholder="t('signup.emailPlaceholder')"
              >
            </div>

            <div class="form-group">
              <label for="password">{{ t('signup.password') }}</label>
              <div class="password-input-container">
                <input 
                  :type="showPassword ? 'text' : 'password'" 
                  id="password" 
                  v-model="password" 
                  required
                  :placeholder="t('signup.passwordPlaceholder')"
                >
                <span class="password-toggle" @click="togglePasswordVisibility">
                  <i :class="showPassword ? 'eye-icon eye-slash' : 'eye-icon'"></i>
                </span>
              </div>
            </div>

            <div class="form-group">
              <label for="confirmPassword">{{ t('signup.confirmPassword') }}</label>
              <div class="password-input-container">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  id="confirmPassword" 
                  v-model="confirmPassword" 
                  required
                  :placeholder="t('signup.confirmPasswordPlaceholder')"
                >
                <span class="password-toggle" @click="toggleConfirmPasswordVisibility">
                  <i :class="showConfirmPassword ? 'eye-icon eye-slash' : 'eye-icon'"></i>
                </span>
              </div>
            </div>

            <div v-if="isRecaptchaEnabled && captchaError" class="form-group captcha-group">
              <p class="captcha-error">{{ captchaError }}</p>
            </div>

            <div class="form-group checkbox-group">
              <input
                type="checkbox"
                id="marketingOptIn"
                v-model="marketingOptIn"
              >
              <label for="marketingOptIn">{{ t('signup.newsletterOptIn') }}</label>
            </div>

            <button type="submit" :disabled="isLoading">
              {{ isLoading ? t('signup.signingUp') : t('signup.submit') }}
            </button>

            <p class="login-link">
              {{ t('signup.alreadyHaveAccount') }} 
              <router-link to="/signin">{{ t('signup.login') }}</router-link>
            </p>
          </form>

          <div v-if="successMessage" class="verification-instructions">
            <p>{{ t('signup.checkEmail') }}</p>
            <button @click="resendVerification" :disabled="isResending || resendCooldown">
              {{ isResending ? t('signup.resending') : t('signup.resendVerification') }}
            </button>
          </div>
        </div>
      </section>
    </main>

    <SharewellFooter />
  </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import SharewellFooter from '../sharewell_footer.vue'

const PAGE_COPY = {
  en: {
    intro: 'Create an optional account to save cards, revisit drafts, and download your PDFs later.'
  },
  fr: {
    intro: 'Créez un compte optionnel pour sauvegarder vos cartes, reprendre vos brouillons et télécharger vos PDF plus tard.'
  },
  es: {
    intro: 'Crea una cuenta opcional para guardar tarjetas, retomar borradores y descargar tus PDF más tarde.'
  }
}

export default {
  name: 'AccountSignup',
  components: { SharewellFooter },
  setup() {
    const { locale, t } = useI18n()
    const pageCopy = computed(() => PAGE_COPY[locale.value] || PAGE_COPY.en)

    const email = ref('')
    const password = ref('')
    const confirmPassword = ref('')
    const marketingOptIn = ref(false)
    const errorMessage = ref('')
    const passwordErrors = ref([])
    const successMessage = ref('')
    const isLoading = ref(false)
    const isResending = ref(false)
    const resendCooldown = ref(false)
    const showPassword = ref(false)
    const showConfirmPassword = ref(false)
    const recaptchaToken = ref('')
    const captchaError = ref('')
    const isRecaptchaEnabled = Boolean(process.env.RECAPTCHA_SITE_KEY)
    
    function resetRecaptcha() {
      if (!isRecaptchaEnabled) return
      recaptchaToken.value = ''
    }

    // Ensure reCAPTCHA v3 script is loaded and ready
    function loadRecaptchaV3() {
      return new Promise((resolve, reject) => {
        if (typeof window === 'undefined' || !isRecaptchaEnabled) {
          resolve()
          return
        }

        // Already available: resolve via grecaptcha.ready
        if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
          window.grecaptcha.ready(() => resolve())
          return
        }

        const timeoutMs = 10000
        let script = document.querySelector('script[data-sharewell-recaptcha-v3]')

        const onReady = () => {
          if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
            window.grecaptcha.ready(() => resolve())
          } else {
            resolve()
          }
        }

        const handleLoad = () => {
          cleanup()
          onReady()
        }

        const handleError = () => {
          cleanup()
          reject(new Error('recaptcha_script_error'))
        }

        const timeoutId = setTimeout(() => {
          cleanup()
          reject(new Error('recaptcha_script_timeout'))
        }, timeoutMs)

        function cleanup() {
          clearTimeout(timeoutId)
          if (script) {
            script.removeEventListener('load', handleLoad)
            script.removeEventListener('error', handleError)
          }
        }

        if (!script) {
          script = document.createElement('script')
          script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(process.env.RECAPTCHA_SITE_KEY)}`
          script.async = true
          script.defer = true
          script.setAttribute('data-sharewell-recaptcha-v3', 'true')
          script.addEventListener('load', handleLoad)
          script.addEventListener('error', handleError)
          document.head.appendChild(script)
          return
        }

        // Script tag exists. If grecaptcha is already present, resolve immediately.
        if (window.grecaptcha) {
          cleanup()
          onReady()
          return
        }

        // Otherwise, wait for existing script load or error.
        script.addEventListener('load', handleLoad)
        script.addEventListener('error', handleError)
      })
    }

    onMounted(() => {
      // Load reCAPTCHA v3 on mount
      loadRecaptchaV3()
    })

    function togglePasswordVisibility() {
      showPassword.value = !showPassword.value
    }

    function toggleConfirmPasswordVisibility() {
      showConfirmPassword.value = !showConfirmPassword.value
    }

    function startCooldown() {
      resendCooldown.value = true
      setTimeout(() => {
        resendCooldown.value = false
      }, 5000)
    }

    async function handleSubmit() {
      errorMessage.value = ''
      passwordErrors.value = []
      captchaError.value = ''

      if (password.value !== confirmPassword.value) {
        errorMessage.value = t('signup.passwordsDoNotMatch')
        return
      }

      // For v3, we request a fresh token right before submit
      if (isRecaptchaEnabled) {
        try {
          await loadRecaptchaV3()
          if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
            recaptchaToken.value = await window.grecaptcha.execute(process.env.RECAPTCHA_SITE_KEY, { action: 'signup' })
          }
        } catch {
          captchaError.value = t('signup.recaptchaFailed')
          return
        }
      }

      try {
        isLoading.value = true
        const payload = {
          email: email.value,
          password: password.value,
          marketing_opt_in: marketingOptIn.value,
        }

        if (isRecaptchaEnabled) {
          payload.recaptcha_token = recaptchaToken.value
        }

        const response = await axios.post('/auth/signup', payload)
        successMessage.value = response.data.message
        startCooldown()
        resetRecaptcha()
      } catch (error) {
        if (error.response && error.response.data) {
          const { error: serverError, details, code } = error.response.data
          if (code === 'recaptcha_failed' || code === 'recaptcha_missing') {
            captchaError.value = serverError
            resetRecaptcha()
            return
          }
          errorMessage.value = serverError
          if (details) {
            passwordErrors.value = details
          }
        } else {
          errorMessage.value = t('signup.generalError')
        }
      } finally {
        isLoading.value = false
      }
    }

    async function resendVerification() {
      try {
        isResending.value = true
        errorMessage.value = ''
        await axios.post('/auth/resend-verification', { email: email.value })
        successMessage.value = t('signup.verificationEmailResent')
        startCooldown()
      } catch (error) {
        errorMessage.value = (error.response && error.response.data && error.response.data.error) || t('signup.generalError')
      } finally {
        isResending.value = false
      }
    }

    return {
      t,
      email,
      password,
      confirmPassword,
      marketingOptIn,
      errorMessage,
      passwordErrors,
      pageCopy,
      successMessage,
      isLoading,
      isResending,
      resendCooldown,
      showPassword,
      showConfirmPassword,
      captchaError,
      isRecaptchaEnabled,
      togglePasswordVisibility,
      toggleConfirmPasswordVisibility,
      handleSubmit,
      resendVerification
    }
  }
}
</script>

<style scoped>
.auth-page {
  --ink: #302a27;
  --ink-soft: #6c6159;
  --ink-muted: #92857b;
  --paper: #fff8f0;
  --paper-cream: #fffdf7;
  --paper-line: rgba(87, 65, 52, 0.16);
  --title-red: #c94750;
  --coral: #e75a62;
  --coral-hover: #d64b55;
  --sage: #789170;
  --aqua: #d9f3f2;
  display: flow-root;
  min-height: 100vh;
  color: var(--ink);
  background: transparent;
  font-family: "Sharewell Glacial", "Avenir Next", "Helvetica Neue", Arial, sans-serif;
}

.auth-page::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255, 253, 247, 0.04), rgba(255, 253, 247, 0.72));
}

.auth-page > * {
  position: relative;
  z-index: 1;
}

.auth-main {
  width: min(960px, calc(100% - 48px));
  display: grid;
  grid-template-columns: minmax(270px, 0.78fr) minmax(340px, 1fr);
  gap: clamp(24px, 3.6vw, 46px);
  align-items: center;
  border: 1px solid rgba(87, 65, 52, 0.14);
  border-radius: 12px;
  background: rgba(255, 253, 247, 0.78);
  box-shadow: 0 28px 54px -42px rgba(63, 44, 34, 0.38);
  margin: clamp(42px, 6vw, 78px) auto clamp(56px, 7vw, 96px);
  padding: clamp(28px, 3.6vw, 42px);
}

.auth-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.auth-lede {
  max-width: 30ch;
  margin: clamp(14px, 2vw, 20px) auto 0;
  color: var(--ink-muted);
  font-size: clamp(0.9rem, 1vw, 0.98rem);
  line-height: 1.5;
  text-align: center;
}

.auth-art {
  position: relative;
  width: min(330px, 100%);
  min-height: clamp(230px, 28vw, 330px);
  margin: 0 auto;
}

.auth-art img {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  filter: drop-shadow(0 20px 16px rgba(68, 48, 34, 0.14));
}

.auth-panel {
  min-width: 0;
}

.form-section {
  max-width: 450px;
  margin: 0 auto;
}

h2 {
  margin: 0 0 24px;
  color: var(--title-red);
  font-family: "DM Serif Text", Georgia, serif;
  font-size: clamp(2.1rem, 3vw, 3.1rem);
  font-weight: 400;
  line-height: 1;
  text-align: left;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  color: var(--ink);
  font-weight: 800;
}

input[type='text'],
input[type='email'],
input[type='password'] {
  width: 100%;
  min-height: 48px;
  border: 1px solid var(--paper-line);
  border-radius: 10px;
  background: #ffffff;
  color: var(--ink);
  font-size: 16px;
  padding: 0 14px;
  box-sizing: border-box;
}

input[type='text']:focus,
input[type='email']:focus,
input[type='password']:focus {
  outline: none;
  border-color: var(--coral);
  box-shadow: inset 0 0 0 1px var(--coral);
}

.checkbox-group {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}

.checkbox-group input[type='checkbox'] {
  appearance: none;
  width: 20px;
  height: 20px;
  display: grid;
  place-items: center;
  border: 1px solid var(--paper-line);
  border-radius: 6px;
  background: #ffffff;
  margin: 1px 0 0;
  cursor: pointer;
}

.checkbox-group input[type='checkbox']::after {
  content: "";
  width: 6px;
  height: 10px;
  margin-top: -2px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) scale(0);
}

.checkbox-group input[type='checkbox']:checked {
  border-color: var(--coral);
  background: var(--coral);
}

.checkbox-group input[type='checkbox']:checked::after {
  transform: rotate(45deg) scale(1);
}

.checkbox-group label {
  display: inline;
  margin-bottom: 0;
  color: var(--ink-soft);
  font-weight: 700;
  line-height: 1.45;
}

button {
  width: 100%;
  min-height: 48px;
  border: 1px solid var(--coral);
  border-radius: 14px;
  background: var(--coral);
  color: var(--paper-cream);
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition:
    background-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    border-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

button:hover {
  border-color: var(--coral-hover);
  background: var(--coral-hover);
  transform: translateY(-2px);
}

button:disabled {
  border-color: rgba(87, 65, 52, 0.16);
  background: rgba(87, 65, 52, 0.18);
  cursor: not-allowed;
  transform: none;
}

.error-message,
.captcha-error {
  color: var(--title-red);
}

.error-message {
  margin-bottom: 20px;
  padding: 12px 14px;
  border: 1px solid rgba(201, 71, 80, 0.22);
  border-radius: 10px;
  background: rgba(201, 71, 80, 0.08);
}

.password-errors {
  margin: 10px 0 0 20px;
  padding: 0;
  font-size: 14px;
}

.password-errors li {
  margin-bottom: 4px;
}

.captcha-group {
  margin-bottom: 20px;
}

.captcha-error {
  margin: 0;
  font-size: 14px;
}

.login-link {
  text-align: center;
  margin-top: 20px;
  color: var(--ink-soft);
  line-height: 1.5;
}

.login-link a,
.notice-link {
  color: var(--title-red);
  font-weight: 800;
  text-decoration: none;
}

.login-link a:hover,
.notice-link:hover {
  text-decoration: underline;
}

.verification-instructions {
  text-align: left;
}

.verification-instructions p {
  color: var(--ink-soft);
  line-height: 1.6;
}

.verification-instructions button {
  margin-top: 12px;
}

.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-container input {
  padding-right: 42px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.eye-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'%3E%3Cpath fill='%236c6159' d='M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z'/%3E%3C/svg%3E") no-repeat center center;
  background-size: contain;
}

.eye-slash {
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512'%3E%3Cpath fill='%236c6159' d='M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z'/%3E%3C/svg%3E") no-repeat center center;
  background-size: contain;
}

.no-account-notice {
  margin-bottom: 24px;
  border: 1px solid rgba(120, 145, 112, 0.22);
  border-radius: 12px;
  background: rgba(217, 243, 242, 0.34);
  padding: 16px;
}

.notice-content {
  display: flex;
  align-items: flex-start;
}

.notice-text strong {
  color: var(--ink);
  font-size: 1rem;
  display: block;
  margin-bottom: 6px;
}

.notice-text p {
  color: var(--ink-soft);
  margin: 0;
  font-size: 0.94rem;
  line-height: 1.48;
}

@media (max-width: 900px) {
  .auth-main {
    grid-template-columns: 1fr;
    min-height: auto;
    align-items: start;
  }

  .auth-lede {
    max-width: 34ch;
  }

  .auth-art {
    min-height: 250px;
  }
}

@media (max-width: 560px) {
  .auth-main {
    width: min(100% - 28px, 720px);
    padding: 26px 20px;
  }

  .auth-lede {
    font-size: 1.12rem;
  }

  .auth-art {
    display: none;
  }

}
</style>
